import {pushEvent} from "tracking/core/dispatcher";
import type {
    AudioPlayPayload,
    AudioPausedPayload,
    AudioCompletedPayload,
} from "../core/types";
import {EVENT_NAMES} from "tracking/core/eventNames";

declare global {
    interface Window {
        SC?: any;
    }
}

const SELECTOR = 'iframe[src*="w.soundcloud.com/player/"]';
const API_SRC = "https://w.soundcloud.com/player/api.js";
const PLAY_DEDUPE_MS = 1000;

const TRACK_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
const MAX_TRACK_CACHE_SIZE = 500;

const SOUND_DATA_TIMEOUT_MS = 1000;

const boundIframes = new WeakSet<HTMLIFrameElement>();
const finishedTracks = new Map<string, number>();
const lastPlayAt = new Map<string, number>();

let observer: MutationObserver | null = null;

type AudioPayload = AudioPlayPayload | AudioPausedPayload | AudioCompletedPayload;

function getTrackId(sound: any) {
    return String(
        sound?.id ||
        sound?.permalink_url ||
        sound?.title ||
        window.location.href
    );
}

function cleanupTrackCache() {
    const now = Date.now();

    [lastPlayAt, finishedTracks].forEach(map => {
        map.forEach((ts, key) => {
            if (now - ts > TRACK_CACHE_TTL_MS) {
                map.delete(key);
            }
        });

        while (map.size > MAX_TRACK_CACHE_SIZE) {
            const nextKey = map.keys().next();
            if (nextKey.done) break;

            const oldestKey = nextKey.value;
            map.delete(oldestKey);
        }
    });
}

function shouldSkipRecentPlay(trackId: string) {
    cleanupTrackCache();

    const now = Date.now();
    const last = lastPlayAt.get(trackId) || 0;

    if (now - last < PLAY_DEDUPE_MS) return true;

    lastPlayAt.set(trackId, now);
    return false;
}

function createPayload(sound: any, durationMs: number): AudioPayload {
    return {
        duration: Math.round((durationMs || 0) / 1000),
        provider: "SoundCloud",
        audio_title: sound?.title || document.title || undefined,
        url: window.location.href,
    };
}

function withSoundData(widget: any, callback: (durationMs: number, sound: any) => void) {
    let settled = false;

    const timeout = window.setTimeout(() => {
        finish();
    }, SOUND_DATA_TIMEOUT_MS);

    function finish(durationMs = 0, sound: any = null) {
        if (settled) return;

        settled = true;
        window.clearTimeout(timeout);
        callback(durationMs, sound);
    }

    try {
        widget.getDuration((durationMs: number) => {
            widget.getCurrentSound((sound: any) => {
                finish(durationMs, sound);
            });
        });
    } catch {
        finish();
    }
}

function ensureApi(callback: () => void) {
    if (window.SC?.Widget) return callback();

    if (document.querySelector(`script[src^="${API_SRC}"]`)) {
        window.setTimeout(() => window.SC?.Widget && callback(), 200);
        return;
    }

    const script = document.createElement("script");
    script.src = API_SRC;
    script.async = true;
    script.onload = () => callback();
    document.head.appendChild(script);
}

function bindIframe(iframe: HTMLIFrameElement) {
    if (!window.SC?.Widget || boundIframes.has(iframe)) return;

    boundIframes.add(iframe);

    const widget = window.SC.Widget(iframe);
    const {READY, PLAY, PAUSE, FINISH} = window.SC.Widget.Events;

    widget.bind(READY, () => {
        widget.bind(PLAY, () => {
            withSoundData(widget, (durationMs, sound) => {
                const trackId = getTrackId(sound);

                if (shouldSkipRecentPlay(trackId)) return;

                finishedTracks.delete(trackId);

                pushEvent(EVENT_NAMES.AUDIO_PLAY, createPayload(sound, durationMs));
            });
        });

        widget.bind(PAUSE, (event: any) => {
            withSoundData(widget, (durationMs, sound) => {
                const trackId = getTrackId(sound);
                const currentPosition = Number(event?.currentPosition || 0);
                const relativePosition = Number(event?.relativePosition || 0);

                const finished =
                    relativePosition >= 0.99 ||
                    (durationMs > 0 && currentPosition >= durationMs - 1000) ||
                    finishedTracks.has(trackId);

                if (finished) return;

                pushEvent(EVENT_NAMES.AUDIO_PAUSED, createPayload(sound, durationMs));
            });
        });

        widget.bind(FINISH, () => {
            withSoundData(widget, (durationMs, sound) => {
                const trackId = getTrackId(sound);

                cleanupTrackCache();
                finishedTracks.set(trackId, Date.now());

                pushEvent(EVENT_NAMES.AUDIO_COMPLETED, createPayload(sound, durationMs));
            });
        });
    });
}

export function trackSoundCloudAudioPlay() {
    if (typeof window === "undefined") return;

    const iframes = Array.from(
        document.querySelectorAll(SELECTOR)
    ) as HTMLIFrameElement[];

    if (!iframes.length) return;

    ensureApi(() => iframes.forEach(bindIframe));
}

function onVisibilityChange() {
    if (!document.hidden) trackSoundCloudAudioPlay();
}

export function startSoundCloudTracking() {
    if (typeof window === "undefined" || observer) return;

    trackSoundCloudAudioPlay();

    observer = new MutationObserver(trackSoundCloudAudioPlay);
    observer.observe(document.documentElement, {childList: true, subtree: true});

    document.addEventListener("visibilitychange", onVisibilityChange);
}

export function stopSoundCloudTracking() {
    if (typeof window === "undefined" || !observer) return;

    observer.disconnect();
    observer = null;

    lastPlayAt.clear();
    finishedTracks.clear();

    document.removeEventListener("visibilitychange", onVisibilityChange);
}